howspace.components.audioPlayer = (() => {
    'use strict';

    const defaultOptions = {
        controls: true,
        autoplay: false,
        preload: 'metadata',
        responsive: true,
        breakpoints: {
            tiny: 100,
            xsmall: 120,
        },
        playbackRates: [
            0.5,
            1,
            1.25,
            1.5,
            1.75,
            2
        ],
        audioOnlyMode: true,
        controlBar: {
            pictureInPictureToggle: false,
            fullscreenToggle: false,
        }
    };

    /**
     * @param {*} audioPlayer
     * @param {*} options
     * @returns
     */
    const initVideoJs = async (audioPlayer, options = {}) => {
        if (audioPlayer.is('.video-js-initialized, [data-is-stored="false"], [data-url=""]')) {
            // VideoJS is already initialized, audio file is not stored or URL is empty.
            return;
        }

        if (!audioPlayer.is('.howspace-audio-player-initialized')) {
            // Audio player is no longer initialized.
            return true;
        }

        await lib('video.js', 'video.min.js');

        audioPlayer.addClass('video-js-initialized');

        audioPlayer.append(
            `<audio class="video-js vjs-default-skin">
                <source type="${audioPlayer.data('content-type')}" src="${audioPlayer.data('url')}">
            </audio>`
        );

        if (!options) {
            options = {};
        }

        let playerOptions = $.extend({}, defaultOptions, options);

        playerOptions.autoplay = false;

        const videoJsElem = audioPlayer.find('.video-js')[0];
        videojs(videoJsElem, playerOptions, () => {
            const videoJsPlayer = videojs.getPlayer(videoJsElem);
            const eventDetail = {
                player : videoJsPlayer
            }

            videoJsPlayer.reloadSourceOnError();

            if (audioPlayer.data('shareable-modal')) {
                const shareableModalUrl = audioPlayer.data('shareable-modal');
                const shareableModalInit = audioPlayer.data('shareable-modal-init');
                const playbackRateMenuButton = videoJsPlayer.controlBar.getChild("PlaybackRateMenuButton");
                const index = videoJsPlayer.controlBar.children().indexOf(playbackRateMenuButton);

                const shareButton = videoJsPlayer.controlBar.addChild("button", {
                    className: "vjs-share-button",
                    controlText: howspace.translate('common_share_a_moment')
                }, index + 1);

                const shareButtonPlaceholder = shareButton.el().getElementsByClassName("vjs-icon-placeholder")[0];
                if (shareButtonPlaceholder) {
                    shareButtonPlaceholder.innerHTML = `<i class="fa-light fa-share"></i>`;
                }
                shareButton.on("click", () => {
                    howspace.components.modal.show(shareableModalUrl, howspace.components.getCallback(shareableModalInit));
                });
            }

            videoJsPlayer.on('play', () => {
                if (audioPlayer.data('start-at-seconds') && !isNaN(audioPlayer.data('start-at-seconds'))) {
                    videoJsPlayer.currentTime(audioPlayer.data('start-at-seconds'));
                    audioPlayer.dataAndAttr('start-at-seconds', null);
                }

                audioPlayer.addClass('has-started');
                howspace.components.videoPlayer.pauseOtherPlayers(audioPlayer);
                howspace.dispatchEvent(audioPlayer, howspace.components.audioPlayer.AUDIO_PLAY, eventDetail);
            });

            videoJsPlayer.on('playing', () => {
                audioPlayer.addClass('playing');
                howspace.activityTracker.setPlaybackActive(true);
            });

            videoJsPlayer.on('timeupdate', () => {
                audioPlayer.dataAndAttr('current-time', videoJsPlayer.currentTime());
            });

            videoJsPlayer.on('pause', () => {
                audioPlayer.removeClass('playing');
                howspace.dispatchEvent(audioPlayer, howspace.components.audioPlayer.AUDIO_PAUSE, eventDetail);
                howspace.activityTracker.setPlaybackActive(false);
            });

            if (audioPlayer.data('autoplay') && audioPlayer.data('autoplay') !== false) {
                // Wait 100 ms before calling play, otherwise starting playback randomly fails.
                setTimeout(() => {
                    const promise = videoJsPlayer.play();

                    if (promise !== undefined) {
                        // Sometimes it's possible that the promise will never end up to succeed or fail.
                        // Thus, we use 5 second timeout to fall back to fail use case.
                        let promiseTimeoutId = setTimeout(() => {
                            promiseTimeoutId = null;
                        }, 5000);

                        promise.then(() => {
                            if (promiseTimeoutId !== null) {
                                clearTimeout(promiseTimeoutId);
                                promiseTimeoutId = null;
                            }
                        }).catch(() => {
                            if (promiseTimeoutId !== null) {
                                clearTimeout(promiseTimeoutId);
                                promiseTimeoutId = null;
                            }
                        });
                    }
                }, 100);
            }

            videoJsPlayer.on('ended', () => {
                audioPlayer.removeClass('playing');
                howspace.dispatchEvent(audioPlayer, howspace.components.audioPlayer.AUDIO_ENDED, eventDetail);
            });

            if (typeof options.onTimeUpdate === 'function') {
                howspace.dispatchEvent(audioPlayer, howspace.components.audioPlayer.AUDIO_TIME_UPDATED, eventDetail);
            }

            videoJsPlayer.on('error', () => {
                destroyVideoJs(audioPlayer);
                audioPlayer.addClass('has-error');
            });
        });
    };

    /**
     * @param {*} audioPlayer
     */
    const destroyVideoJs = async (audioPlayer) => {
        if (!audioPlayer.is('.video-js-initialized')) {
            return;
        }

        await lib('video.js', 'video.min.js');

        audioPlayer.removeClass('video-js-initialized');
        audioPlayer.closest('.howspace-audio-player, .howspace-audio-player-wrapper').removeClass('playing');
        videojs(audioPlayer.find('.video-js')[0]).dispose();
    };

    /**
     * @param {*} audioPlayer
     * @param {*} options
     * @returns
     */
    const initAudioPlayer = (audioPlayer, options = {}) => {
        if (audioPlayer.is('.howspace-audio-player-initialized')) {
            return true;
        }
        audioPlayer.addClass('howspace-audio-player-initialized');

        initVideoJs(audioPlayer, options);

        howspace.mediaShare.handleSharingMomentLink();
    };

    /**
     * @param {*} audioPlayer
     * @returns
     */
    const destroyAudioPlayer = (audioPlayer) => {
        if (!audioPlayer.is('.howspace-audio-player-initialized')) {
            return true;
        }

        audioPlayer.removeClass('howspace-audio-player-initialized');

        audioPlayer.off('click');

        if (audioPlayer.is('.video-js-initialized')) {
            destroyVideoJs(audioPlayer);
        }
    };

    real.onTierReady(real.TIER_2, () => {
        real.events.on('uploadedfile:stored', (notification) => {
            // During duplication files are stored asynchronously. Listen for the uploaded file stored events
            // and enable any audio players as soon as the mp4 baseline video file is stored.
            const audioPlayer = $('.howspace-audio-player[data-is-stored="false"][data-uploadedfile-id="' + notification.object.uploadedfile.id + '"]');
            audioPlayer.dataAndAttr('is-stored', true);
            initVideoJs(audioPlayer);
        });
    });

    return {
        AUDIO_PLAY : 'audio-play',
        AUDIO_PAUSE : 'audio-pause',
        AUDIO_TIME_UPDATED : 'audio-time-updated',
        AUDIO_ENDED : 'audio-ended',
        init: (elem, options) => {
            elem.findIncludingSelf('.howspace-audio-player:not(.howspace-audio-player-initialized)').each(function () {
                initAudioPlayer($(this), options);
            });
        },
        destroy: (elem) => {
            elem.findIncludingSelf('.howspace-audio-player.howspace-audio-player-initialized').each(function () {
                destroyAudioPlayer($(this));
            });
        },
        destroyVideoJs: destroyVideoJs
    };

})();
